import * as React from 'react';
import { useRef } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { Label } from '../label.js';
import { Text, textVariants } from '../../Text.js';
import { HStack } from '../../HStack.js';
import { Command as CommandPrimitive } from 'cmdk';
import { VStack } from '../../VStack.js';

const coinInputVariants = cva(
  'flex w-full font-body rounded-md text-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-200 focus-within:outline focus-within:outline-gray-300 placeholder:text-black placeholder:text-opacity-40 placeholder:text-[24px] placeholder:leading-[32px] disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-black',
        error: 'text-red-400',
      },
      textarea: {
        true: 'h-auto p-4',
        false: 'px-2 py-[10px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      textarea: false,
    },
  }
);

export interface CoinInputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    Omit<
      React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
      'value'
    >,
    VariantProps<typeof coinInputVariants> {
  label?: React.ReactNode;
  usdLabel?: string;
  error?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
}

const CoinInput = React.forwardRef<HTMLInputElement, CoinInputProps>(
  (
    {
      className,
      type,
      label,
      usdLabel,
      error,
      leftElement,
      rightElement,
      ...props
    },
    ref
  ) => {
    const id = useRef(Math.random());
    return (
      <VStack
        className={coinInputVariants({
          className: 'w-full',
        })}
      >
        {label && (
          <Label
            htmlFor={props.id ?? id.current.toString()}
            className={textVariants({
              font: 'medium-xs',
              color: error ? 'red' : 'gray-700',
            })}
          >
            {label}
          </Label>
        )}
        <HStack className='gap-1'>
          {leftElement && leftElement}
          <input
            type={type}
            id={props.id ?? id.current.toString()}
            className={textVariants({
              font: 'medium-lg',
              color: error ? 'red' : 'gray-900',
              className:
                'w-full bg-transparent focus:outline-none active:outline-none',
            })}
            ref={ref}
            {...props}
          />
          {rightElement && rightElement}
        </HStack>
        {usdLabel && (
          <Label
            htmlFor={props.id ?? id.current.toString()}
            className={textVariants({
              font: 'sm',
              color: 'gray-600',
            })}
          >
            {usdLabel}
          </Label>
        )}
        {error !== true && error && (
          <Text color={'red'} font={'regular-xs'}>
            {error}
          </Text>
        )}
      </VStack>
    );
  }
);
CoinInput.displayName = 'CoinInput';

export { CoinInput, coinInputVariants };
